import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { CheckCircle2, XCircle, Loader2, Mail, KeyRound } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

/**
 * Email/SMS verification landing page.
 *
 * Two entry modes:
 *   1) ?token=<…>  → user clicked the link in their email; auto-verifies
 *                    via POST /api/auth/verify-email.
 *   2) ?email=<…>&pending=1 → user just registered; shows "check your
 *      email" message + a 6-digit OTP entry form posting to
 *      POST /api/auth/verify-code.
 *
 * After a successful verification (either path), redirects to /portal.
 */
export default function VerifyEmail() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("idle");
  // 'idle' | 'loading' | 'ok' | 'fail' | 'pending' (waiting for code)
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState(params.get("code") || "");
  const [fallbackCode] = useState(params.get("code") || "");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const t = {
    verifying: isAr ? "جارٍ التحقق…" : "Verifying…",
    okTitle: isAr ? "تم تفعيل حسابك" : "Account verified",
    okBody: isAr
      ? "أهلاً بك في مجموعة الدخيل القانونية. سيتم تحويلك خلال لحظات…"
      : "Welcome to Aldakheel Legal Group. Redirecting you shortly…",
    failTitle: isAr ? "تعذّر التفعيل" : "Verification failed",
    failBody: isAr
      ? "الرابط منتهي الصلاحية أو غير صحيح. يمكنك طلب رابط جديد."
      : "The link is invalid or expired. You can request a new one.",
    requestNew: isAr ? "طلب رابط جديد" : "Request a new link",
    backHome: isAr ? "العودة للرئيسية" : "Back to home",
    // Pending / code-entry mode:
    pendingEyebrow: isAr ? "تفعيل الحساب" : "Activate your account",
    pendingTitle: isAr ? "راجع بريدك الإلكتروني" : "Check your email",
    pendingBody: isAr
      ? "أرسلنا رمز تحقّق مكوّن من 6 أرقام إلى بريدك الإلكتروني (وإلى هاتفك إن قدّمت رقماً). أدخل الرمز أدناه لتفعيل حسابك."
      : "We sent a 6-digit verification code to your email (and to your phone if provided). Enter the code below to activate your account.",
    emailLabel: isAr ? "البريد الإلكتروني" : "Email",
    codeLabel: isAr ? "رمز التحقق (6 أرقام)" : "Verification code (6 digits)",
    verifyBtn: isAr ? "تأكيد الرمز" : "Verify code",
    verifyBusy: isAr ? "جارٍ التأكيد..." : "Verifying…",
    resendBtn: isAr ? "إعادة إرسال الرمز" : "Resend code",
    resendBusy: isAr ? "جارٍ الإرسال..." : "Sending…",
    resendDone: isAr
      ? "تم الإرسال. تحقّق من بريدك خلال دقائق."
      : "Sent. Check your inbox within a few minutes.",
    needEmail: isAr ? "الرجاء إدخال البريد الإلكتروني." : "Please enter your email.",
    needCode: isAr ? "الرجاء إدخال رمز التحقق." : "Please enter the verification code.",
    helpHint: isAr
      ? "لم يصلك الرمز؟ تحقّق من مجلد البريد العشوائي، أو اضغط «إعادة إرسال الرمز»."
      : "Didn't receive the code? Check your spam folder, or click \"Resend code\".",
  };

  // We intentionally only re-run when the URL params or navigator
  // change. `api` is a stable module-level axios instance (singleton);
  // `setState` and `setErrorMsg` are React state setters which are
  // guaranteed stable. Including any of them in the dependency array
  // would cause a redundant verify-call on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const token = params.get("token");
    const nextParam = params.get("next");
    const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : "/portal";
    if (token) {
      // Legacy / email-link path: auto-verify on landing.
      setState("loading");
      api.post("/auth/verify-email", { token })
        .then(() => {
          setState("ok");
          setTimeout(() => nav(safeNext), 2500);
        })
        .catch((err) => {
          setState("fail");
          setErrorMsg(err?.response?.data?.detail || "");
        });
    } else {
      // OTP-code path: visitor arrives from /register or /resend-verification.
      setState("pending");
    }
  }, [params, nav]);

  const submitCode = async (e) => {
    e?.preventDefault();
    if (!email) return toast.error(t.needEmail);
    if (!code || code.length < 4) return toast.error(t.needCode);
    setVerifying(true);
    try {
      await api.post("/auth/verify-code", { email, code });
      setState("ok");
      const nextParam = params.get("next");
      const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : "/portal";
      setTimeout(() => nav(safeNext), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.failTitle);
    } finally {
      setVerifying(false);
    }
  };

  const resendCode = async () => {
    if (!email) return toast.error(t.needEmail);
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success(t.resendDone);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "");
    } finally {
      setResending(false);
    }
  };

  return (
    <div data-testid="verify-page" className="max-w-md mx-auto px-6 py-24">
      {state === "loading" && (
        <div className="text-center py-12">
          <Loader2 className="w-10 h-10 animate-spin mx-auto text-slate-400" />
          <p className="mt-6 text-slate-600">{t.verifying}</p>
        </div>
      )}

      {state === "ok" && (
        <div data-testid="verify-success" className="text-center py-8">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
          <h1 className="font-display text-3xl mt-6">{t.okTitle}</h1>
          <p className="mt-3 text-slate-600">{t.okBody}</p>
        </div>
      )}

      {state === "fail" && (
        <div data-testid="verify-failure" className="text-center py-8">
          <XCircle className="w-14 h-14 mx-auto text-red-500" />
          <h1 className="font-display text-3xl mt-6">{t.failTitle}</h1>
          <p className="mt-3 text-slate-600">{t.failBody}</p>
          {errorMsg && <p className="mt-2 text-xs text-slate-500">{errorMsg}</p>}
          <div className="mt-8 flex flex-col gap-3">
            <Link
              to="/resend-verification"
              className="inline-block bg-slate-900 hover:bg-slate-800 text-white px-6 h-11 leading-[44px]"
              data-testid="verify-request-new"
            >
              {t.requestNew}
            </Link>
            <Link to="/" className="text-sm text-slate-500 underline underline-offset-4">
              {t.backHome}
            </Link>
          </div>
        </div>
      )}

      {state === "pending" && (
        <div data-testid="verify-pending">
          <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-amber-50 border border-amber-200">
            <Mail className="w-7 h-7 text-amber-700" />
          </div>
          <p className="eyebrow text-center mt-6">{t.pendingEyebrow}</p>
          <h1 className="font-display text-3xl mt-2 tracking-tight text-center">
            {t.pendingTitle}
          </h1>
          <p className="mt-4 text-slate-600 text-center leading-relaxed">
            {t.pendingBody}
          </p>

          {fallbackCode ? (
            <div
              className="mt-6 border border-amber-300 bg-amber-50 p-4 rounded-sm text-center"
              data-testid="verify-fallback-banner"
            >
              <p className="text-xs text-amber-900 leading-relaxed">
                {isAr
                  ? "تعذّر إرسال البريد مؤقتاً. هذا رمز التفعيل الخاص بك:"
                  : "Email delivery is temporarily down. Your verification code:"}
              </p>
              <p
                className="font-mono text-3xl tracking-[0.5em] text-amber-900 mt-2"
                data-testid="verify-fallback-code"
              >
                {fallbackCode}
              </p>
            </div>
          ) : null}

          <form onSubmit={submitCode} className="mt-10 grid gap-5">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">
                {t.emailLabel}
              </Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                dir="ltr"
                data-testid="verify-email-input"
                className="mt-2 rounded-sm h-11"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">
                {t.codeLabel}
              </Label>
              <div className="relative mt-2">
                <KeyRound className="absolute top-3 start-3 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  required
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  dir="ltr"
                  data-testid="verify-code-input"
                  className="rounded-sm h-12 ps-10 text-center font-mono text-xl tracking-[0.5em]"
                  placeholder="••••••"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={verifying}
              data-testid="verify-submit"
              className="rounded-sm h-12 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {verifying ? t.verifyBusy : t.verifyBtn}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-slate-500">{t.helpHint}</p>
            <button
              type="button"
              onClick={resendCode}
              disabled={resending}
              data-testid="verify-resend"
              className="mt-3 text-sm text-slate-900 underline underline-offset-4 disabled:opacity-50"
            >
              {resending ? t.resendBusy : t.resendBtn}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
